/**
 * DocumentsSection Component
 * Displays and manages documents for a property, unit, or lease
 */

import { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Download, Trash2 } from 'lucide-react';
import { documentsApi, type DocumentResponse } from '../../lib/api';
import { DocumentUploadModal } from './DocumentUploadModal';
import { PageSection } from '../layout/index';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface DocumentsSectionProps {
  scope: 'PROPERTY' | 'UNIT' | 'LEASE';
  propertyId?: string;
  unitId?: string;
  leaseId?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getMimeTypeBadge(mimeType: string): string {
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('image')) return 'IMG';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('text')) return 'TXT';
  return 'FILE';
}

export function DocumentsSection({ scope, propertyId, unitId, leaseId }: DocumentsSectionProps) {
  const [documents, setDocuments] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Delete confirm state
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Toggle tenant-hidden state
  const [togglingDocId, setTogglingDocId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let result: { documents: DocumentResponse[] };
      if (scope === 'PROPERTY' && propertyId) {
        result = await documentsApi.listForProperty(propertyId);
      } else if (scope === 'UNIT' && unitId) {
        result = await documentsApi.listForUnit(unitId);
      } else if (scope === 'LEASE' && leaseId) {
        result = await documentsApi.listForLease(leaseId);
      } else {
        setDocuments([]);
        return;
      }
      setDocuments(result.documents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [scope, propertyId, unitId, leaseId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleToggleTenantHidden = async (doc: DocumentResponse) => {
    setToggleError(null);
    setTogglingDocId(doc.id);
    try {
      await documentsApi.update(doc.id, { tenantHidden: !doc.tenantHidden });
      await loadDocuments();
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update document visibility');
    } finally {
      setTogglingDocId(null);
    }
  };

  const handleDelete = async (docId: string) => {
    setDeleteError(null);
    try {
      await documentsApi.delete(docId);
      setDeletingDocId(null);
      await loadDocuments();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  return (
    <>
      <PageSection
        title="Documents"
        badge={documents.length > 0 ? documents.length : undefined}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsUploadModalOpen(true)}
            data-testid="upload-document-button"
          >
            Upload
          </Button>
        }
        data-testid="documents-section"
      >
        <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
          {error && (
            <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md">
              {error}
            </div>
          )}

          {deleteError && (
            <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md">
              {deleteError}
            </div>
          )}

          {toggleError && (
            <div
              role="alert"
              className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md"
            >
              {toggleError}
            </div>
          )}

          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading documents...
            </div>
          ) : documents.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="documents-empty">
              <p className="m-0">No documents yet. Click "Upload" to add one.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="documents-list">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="p-3 bg-accent/50 rounded-md border border-border"
                  data-testid="document-item"
                >
                  {deletingDocId === doc.id ? (
                    /* Delete Confirm */
                    <div>
                      <p className="m-0 mb-3 text-sm text-foreground">
                        Are you sure you want to delete "<strong>{doc.name}</strong>"? This cannot be undone.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="default"
                          onClick={() => { setDeletingDocId(null); setDeleteError(null); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="default"
                          onClick={() => handleDelete(doc.id)}
                          data-testid="delete-document-confirm-button"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display View */
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                            {getMimeTypeBadge(doc.mimeType)}
                          </Badge>
                          <h3 className="m-0 text-sm font-medium text-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                            {doc.name}
                          </h3>
                          {scope === 'UNIT' && doc.tenantHidden && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 shrink-0 bg-warning/15 text-warning border-warning/25"
                              data-testid="tenant-hidden-badge"
                            >
                              Hidden from tenant
                            </Badge>
                          )}
                        </div>
                        {doc.description && (
                          <p className="m-0 mb-0.5 text-xs text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                            {doc.description}
                          </p>
                        )}
                        <p className="m-0 text-xs text-muted-foreground/70">
                          {doc.fileName} &bull; {formatFileSize(doc.fileSize)}
                        </p>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {scope === 'UNIT' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleTenantHidden(doc)}
                            disabled={togglingDocId === doc.id}
                            data-testid="toggle-tenant-hidden-button"
                            aria-label={
                              togglingDocId === doc.id
                                ? 'Updating visibility…'
                                : doc.tenantHidden
                                  ? 'Make visible to tenant'
                                  : 'Hide from tenant'
                            }
                          >
                            {togglingDocId === doc.id ? (
                              <><EyeOff className="h-4 w-4" /><span className="hidden sm:inline">Updating…</span></>
                            ) : doc.tenantHidden ? (
                              <><Eye className="h-4 w-4" /><span className="hidden sm:inline">Make visible</span></>
                            ) : (
                              <><EyeOff className="h-4 w-4" /><span className="hidden sm:inline">Hide from tenant</span></>
                            )}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={documentsApi.viewUrl(doc.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="view-document-link"
                            aria-label="View document"
                          >
                            <Eye className="h-4 w-4" />
                            <span className="hidden sm:inline">View</span>
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={documentsApi.downloadUrl(doc.id)}
                            download
                            data-testid="download-document-link"
                            aria-label="Download document"
                          >
                            <Download className="h-4 w-4" />
                            <span className="hidden sm:inline">Download</span>
                          </a>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setDeletingDocId(doc.id); setDeleteError(null); }}
                          data-testid="delete-document-button"
                          aria-label="Delete document"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PageSection>

      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        scope={scope}
        propertyId={propertyId}
        unitId={unitId}
        leaseId={leaseId}
        onSuccess={loadDocuments}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </>
  );
}

export default DocumentsSection;
